import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation } from '@apollo/client/react';
import { Link, useNavigate } from 'react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataGrid } from '@/components/ui/DataGrid';
import { FormField } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { formatDateTime, formatShortId } from '@/lib/format';
import { GuardSuggestion } from '@/components/GuardSuggestion';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { useEmailGuard } from '@/lib/useEmailCheck';
import {
  ticketKindLabel,
  ticketStatusLabel,
  ticketStatusTone,
} from '@/lib/labels';
import { Can } from '@/lib/permissions';
import { useListHref } from '@/hooks/useListLocation';
import {
  canChangeCompanionEmail,
  CHANGE_COMPANION_EMAIL_CONSEQUENCE,
  ChangeCompanionEmailDoc,
  ticketSaleChannelLabel,
  type AdminTicketGridRow,
} from './adminTickets';

export function AdminTicketsGrid({
  rows,
  loading,
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onChanged,
  showNight = false,
  emptyState,
}: {
  rows: AdminTicketGridRow[];
  loading?: boolean;
  page: number;
  pageSize: number;
  totalRows: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onChanged: () => void | Promise<unknown>;
  showNight?: boolean;
  emptyState?: ReactNode;
}) {
  const [target, setTarget] = useState<AdminTicketGridRow | null>(null);
  const navigate = useNavigate();
  // The list as it is now, passed to the ticket page as `state.from`. Its Back link only accepts a `/tickets` path, so a
  // ticket opened from a night page's grid goes back to the bare /tickets list.
  const listHref = useListHref();

  const columns = useMemo<ColumnDef<AdminTicketGridRow>[]>(() => {
    const cols: ColumnDef<AdminTicketGridRow>[] = [
      {
        id: 'ticket',
        header: 'Ticket',
        cell: ({ row }) => (
          <Link
            to={`/tickets/${row.original.id}`}
            state={{ from: listHref }}
            className="font-medium text-gray-900 hover:text-brand dark:text-white"
          >
            #{formatShortId(row.original.id)}
          </Link>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge tone={ticketStatusTone(row.original.status)}>
            {ticketStatusLabel(row.original.status)}
          </Badge>
        ),
      },
    ];
    if (showNight) {
      cols.push({
        id: 'night',
        header: 'Night',
        cell: ({ row }) => (
          <Link
            to={`/nights/${row.original.nightId}`}
            className="text-brand hover:underline"
          >
            {row.original.nightTitle}
          </Link>
        ),
      });
    }
    cols.push(
      {
        id: 'stage',
        header: 'Stage',
        cell: ({ row }) => row.original.stageName,
      },
      {
        id: 'kind',
        header: 'Type',
        cell: ({ row }) => ticketKindLabel(row.original.kind),
      },
      {
        id: 'channel',
        header: 'Channel',
        cell: ({ row }) => (
          <span id={`admin.tickets.channel.${row.original.id}`}>{ticketSaleChannelLabel(row.original.saleChannel)}</span>
        ),
      },
      {
        id: 'buyerEmail',
        header: 'Buyer',
        cell: ({ row }) => (
          <span id={`admin.tickets.buyer.${row.original.id}`}>
            {row.original.buyerEmail || '—'}
          </span>
        ),
      },
      {
        id: 'claimerEmail',
        header: 'Claimer',
        cell: ({ row }) => (
          <span id={`admin.tickets.claimerEmail.${row.original.id}`}>
            {row.original.claimerEmail || '—'}
          </span>
        ),
      },
      {
        id: 'purchasedAt',
        header: 'Purchased',
        cell: ({ row }) => formatDateTime(row.original.purchasedAt),
      },
      {
        id: 'claimedAt',
        header: 'Claimed',
        cell: ({ row }) => formatDateTime(row.original.claimedAt),
      },
      {
        id: 'createdAt',
        header: 'Created',
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
      {
        id: 'order',
        header: 'Order',
        cell: ({ row }) =>
          row.original.orderId ? (
            <Link
              to={`/orders/${row.original.orderId}`}
              className="text-brand hover:underline"
            >
              #{formatShortId(row.original.orderId)}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) =>
          canChangeCompanionEmail(row.original) ? (
            <Can permission="ticket:manage">
              <Button
                id="admin.tickets.changeEmail"
                variant="outline"
                size="sm"
                onClick={() => setTarget(row.original)}
              >
                Change email
              </Button>
            </Can>
          ) : null,
      },
    );
    return cols;
  }, [showNight, listHref]);

  return (
    <>
      <DataGrid<AdminTicketGridRow>
        data={rows}
        columns={columns}
        loading={loading}
        rowId={(row) => row.id}
        rowDomId={(row) => `admin.tickets.row.${row.id}`}
        emptyState={emptyState}
        pagination={{
          page,
          pageSize,
          totalRows,
          totalPages,
          onPageChange,
          onPageSizeChange,
        }}
        onRowClick={(row) => navigate(`/tickets/${row.id}`, { state: { from: listHref } })}
      />
      <ChangeCompanionEmailModal
        key={target?.id ?? 'closed'}
        ticket={target}
        onClose={() => setTarget(null)}
        onChanged={async () => {
          setTarget(null);
          await onChanged();
        }}
      />
    </>
  );
}

export function ChangeCompanionEmailModal({
  ticket,
  onClose,
  onChanged,
}: {
  ticket: AdminTicketGridRow | null;
  onClose: () => void;
  onChanged: () => void | Promise<unknown>;
}) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [changeEmail, { loading }] = useMutation(ChangeCompanionEmailDoc);
  const [checking, setChecking] = useState(false);
  const emailGuard = useEmailGuard();

  const open = ticket !== null;
  const current = ticket?.holderEmail ?? '';

  const submit = async (address: string) => {
    if (!ticket) return;
    setError(null);
    setChecking(true);
    try {
      if (!(await emailGuard.screen(address))) return;
      await changeEmail({ variables: { ticketId: ticket.id, email: address } });
      setEmail('');
      await onChanged();
    } catch (err) {
      emailGuard.refused(err, address);
      setError(graphqlErrorMessage(err));
    } finally {
      setChecking(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change claimer e-mail"
      description={CHANGE_COMPANION_EMAIL_CONSEQUENCE}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            id="admin.tickets.changeEmailConfirm"
            loading={loading || checking}
            disabled={!email.trim() || email.trim() === current}
            onClick={() => void submit(email.trim())}
          >
            Change email
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormField label="Current address">
          <p className="text-sm text-gray-700 dark:text-gray-200">
            {current || '—'}
          </p>
        </FormField>
        <FormField label="New address" required htmlFor="admin.tickets.newEmail">
          <Input
            id="admin.tickets.newEmail"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              emailGuard.changed();
            }}
            placeholder="claimer@example.com"
          />
        </FormField>
        <GuardSuggestion
          guard={emailGuard}
          idPrefix="admin.tickets.changeEmail"
          disabled={loading || checking}
          onAnswer={(address) => {
            setEmail(address);
            void submit(address);
          }}
        />
        {error ? (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        ) : null}
      </div>
    </Modal>
  );
}
